import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ApiVisa } from 'blg-akaun-ts-lib';
import { SessionActions } from '../../modules/session/session-controller/actions';
import { SessionSelectors } from '../../modules/session/session-controller/selectors';
import { SessionStates } from '../../modules/session/session-controller/states';
import { DropDownService } from '../../services/drop-down-service';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ListingInputModel } from '../../models/listing-input.model';
import { ListingService } from '../../services/listing-service';

@Component({
  selector: 'app-forex-data-source-drop-down',
  templateUrl: './forex-data-source-drop-down.component.html',
  styleUrls: ['./forex-data-source-drop-down.component.css'],
})
export class ForexDataSourceComponent implements OnInit, OnDestroy {

  @Input() forexDataSource: FormControl;
  @Input() currencyRate: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() selectedGuid: string;
  @Input() label = 'Forex Data Source';
  @Input() options: any;
  @Input() isSales = true;
  @Input() baseCurrency: any = 'MYR'; 
  @Input() foreignCurrency: string = 'MYR'; // Optional: If you want to target 1 currency and get all the possible currencies to convert to this specified currency, we can use this. 
  @Input() filterByForeignCurrency: boolean = false; // Optional: to filter by foreign currency, set it to "true". If not provided, it will set to "false". 

  @Output() forexDataSourceSelected = new EventEmitter<any>();

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  selectedRow: any;
  rowData: any = [];
  key: string = "forexDataSource-"+ this.baseCurrency;
  allSelected = false;
  filteredCache: any = [];

  /** flags to set the toggle all checkbox state */
  isIndeterminate = false;
  isChecked = false;
  
  protected _onDestroy = new Subject<void>();

  constructor(private dropDownService: DropDownService,
    private listingService: ListingService, 
    private readonly store: Store<SessionStates>
  ) { }

  ngOnInit() {
    // set initial selection
    this.getRowDataSession();
    this.key = "forexDataSource-" + this.filterByForeignCurrency ? this.foreignCurrency : this.baseCurrency;

    // listen for search field value changes
    this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filtering();
      });
  }

  ngOnChanges() {
    this.key = "forexDataSource-" + this.filterByForeignCurrency ? this.foreignCurrency : this.baseCurrency;
    console.log('ngOnChanges', this.key);
    this.getRowDataSession();
  }

  getRowDataSession() {
    this.store.select(SessionSelectors.selectDropDownRowData).subscribe(resolved => {
      this.rowData = resolved?resolved[this.key]?resolved[this.key]:[]:[];
      if (this.rowData.length === 0) {
        this.getRowData();
      }
      else {
        this.filteredOptions$.next(this.rowData.slice());
      }
    })
  }

  getRowData() {
    let formData = this.getInputModel();
    console.log('Forex data source: getRowData()', formData);
    this.listingService.get("forex-data-source/backoffice-ep", formData, this.apiVisa).pipe(
      
    ).subscribe(resolved => {
      this.rowData = resolved.data;
      this.store.dispatch(SessionActions.selectDropDownRowData({key: this.key, rowData: this.rowData}));
      //fill the drop down
      this.filteredOptions$.next(this.rowData.slice());
    });
  }

  filtering() {
    if (!this.rowData) {
      return;
    }
    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search) {
      this.filteredOptions$.next(this.rowData.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the forexDataSource
    this.filteredOptions$.next(
      this.rowData.filter((option) =>
        (option.code ?
          option.code.toLowerCase().indexOf(search) > -1 : null) ||
        (option.name ?
          option.name.toLowerCase().indexOf(search) > -1 : null))
    );
  }

  onSelect(guid: string) {
    console.log('onSelect forex', guid);
    if (guid) {
      this.forexDataSource.patchValue(guid);
      this.selectedRow = this.rowData.find(row => row.guid === guid);
      this.forexDataSourceSelected.emit(this.selectedRow);
      this.getCurrencyRate(guid);  
    }
  }

  isRequired() {
    let value = this.options['required'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false; 
  }

  isMultiple() {
    let value = this.options['multiple'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false; 
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.filteredOptions$.pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(val => {
        if (selectAllValue) {
          this.forexDataSource.patchValue([...val.map(item => item.guid)]);
        } else {
          this.forexDataSource.patchValue([]);
        }
      });
  }

  setToggleAllCheckboxState() {
    if (!this.isMultiple()) return;
    let filteredLength = 0;
    if (this.forexDataSource && this.forexDataSource.value) {
      this.filteredCache.forEach(el => {
        if (this.forexDataSource.value.indexOf(el) > -1) {
          filteredLength++;
        }
      });
      this.isIndeterminate = filteredLength > 0 && filteredLength < this.filteredCache.length;
      this.isChecked = filteredLength > 0 && filteredLength === this.filteredCache.length;
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  getInputModel() {
    const inputModel = {} as ListingInputModel;
    inputModel.search = "";
    inputModel.searchColumns = ['code', 'name'];
    inputModel.status = ['ACTIVE'];
    inputModel.orderBy = 'code';
    inputModel.order = 'asc';
    inputModel.limit = null;
    inputModel.offset = 0;
    inputModel.calcTotalRecords = false;
    inputModel.showCreatedBy = false;
    inputModel.showUpdatedBy = false;
    inputModel.filterLogical= 'AND';

    if(this.filterByForeignCurrency) {
      inputModel.filterConditions = [
        {
          "filterColumn": "currency_foreign",
          "filterValues" : [this.foreignCurrency],
          "filterOperator" : "="
        }
      ]
    } 
    else {
      inputModel.filters = {'currency_base': this.baseCurrency};
    }
    
    return inputModel;
  }

  getCurrencyRateInputModel(guid) {
    const inputModel = {} as ListingInputModel;
    inputModel.search = "";
    inputModel.searchColumns = [];
    inputModel.status = ['ACTIVE'];
    inputModel.orderBy = 'date_txn';
    inputModel.order = 'desc';
    inputModel.limit = 1;
    inputModel.offset = 0;
    inputModel.calcTotalRecords = false;
    inputModel.showCreatedBy = false;
    inputModel.showUpdatedBy = false;
    inputModel.filterLogical= 'AND';
    inputModel.filters = {'data_source_hdr_guid': guid};

    return inputModel;
  }

  getCurrencyRate(guid) {
    let formData = this.getCurrencyRateInputModel(guid);
    this.listingService.get("forex-data-source-history/backoffice-ep", formData, this.apiVisa).pipe(
      
    ).subscribe(resolved => {
      if (resolved.data && resolved.data.length > 0) {
        if (this.isSales) {
          this.currencyRate.patchValue(resolved.data[0].buy_rate);
        } else {
          this.currencyRate.patchValue(resolved.data[0].sell_rate);
        }
      }
      
    });
  }

}
